import React, { useEffect, useState } from 'react';
import { getDashboardStats } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '32px' },
  card: {
    background: '#fff', padding: '24px', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  cardLabel: { fontSize: '13px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardValue: { fontSize: '32px', fontWeight: '700', color: '#1a202c', marginTop: '4px' },
  section: { marginBottom: '32px' },
  sectionTitle: { fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' },
  placeholder: {
    background: '#fff', padding: '40px', borderRadius: '10px', textAlign: 'center',
    color: '#a0aec0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  table: {
    width: '100%', borderCollapse: 'collapse', background: '#fff',
    borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden',
  },
  th: {
    textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '600',
    color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '2px solid #e2e8f0', background: '#f7fafc',
  },
  td: {
    padding: '12px 16px', fontSize: '14px', color: '#2d3748',
    borderBottom: '1px solid #e2e8f0',
  },
  // Rating badge colors aligned to the Monday board (#12079750290).
  ratingBadge: (rating) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px',
    fontWeight: '600',
    background: rating === 'E' ? '#9078C0' : rating === 'EE' ? '#FDFD96' : rating === 'ME' ? '#32de84' : rating === 'NS' ? '#e2e8f0' : '#FFB6C1',
    color: rating === 'E' ? '#ffffff' : rating === 'EE' ? '#5c4d00' : rating === 'ME' ? '#0f5132' : rating === 'NS' ? '#4a5568' : '#8a1c3b',
  }),
  error: { color: '#e53e3e', padding: '16px', background: '#fff5f5', borderRadius: '8px', marginBottom: '16px' },
  loading: { color: '#718096', padding: '24px', textAlign: 'center' },
};

function formatAZ(isoString) {
  if (!isoString) return '--';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
  } catch { return isoString.split('T')[0]; }
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDashboardStats()
      .then((data) => {
        if (!cancelled) {
          if (data.error) {
            setError(data.error);
          } else {
            setStats(data);
          }
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Failed to load dashboard data.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div>
        <h1 style={styles.heading}>Dashboard</h1>
        <div style={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 style={styles.heading}>Dashboard</h1>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  const {
    configured_scenarios_count = 0,
    active_batches_count = 0,
    total_submissions = 0,
    total_archived = 0,
    total_graded = 0,
    total_failed = 0,
    per_batch_stats = [],
    recent_submissions = [],
  } = stats || {};

  return (
    <div>
      <h1 style={styles.heading}>Dashboard</h1>

      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Configured Cases</div>
          <div style={styles.cardValue}>{configured_scenarios_count}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Submissions</div>
          <div style={styles.cardValue}>{total_submissions}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Active Batches</div>
          <div style={styles.cardValue}>{active_batches_count}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Archived</div>
          <div style={styles.cardValue}>{total_archived}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Graded</div>
          <div style={styles.cardValue}>{total_graded}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Grading Failures</div>
          <div style={{ ...styles.cardValue, color: total_failed > 0 ? '#c53030' : '#1a202c' }}>
            {total_failed}
          </div>
        </div>
      </div>

      {per_batch_stats.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Per-Batch Breakdown</h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Batch</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Archived</th>
                <th style={styles.th}>Graded</th>
                <th style={styles.th}>Failures</th>
                <th style={styles.th}>Last Graded</th>
              </tr>
            </thead>
            <tbody>
              {per_batch_stats.map((b) => (
                <tr key={b.batch}>
                  <td style={{ ...styles.td, fontWeight: '600' }}>Batch {b.batch}</td>
                  <td style={styles.td}>{b.submissions || 0}</td>
                  <td style={styles.td}>{b.archived || 0}</td>
                  <td style={styles.td}>{b.graded || 0}</td>
                  <td style={{
                    ...styles.td,
                    color: (b.failed || 0) > 0 ? '#c53030' : '#2d3748',
                    fontWeight: (b.failed || 0) > 0 ? '600' : '400',
                  }}>
                    {b.failed || 0}
                  </td>
                  <td style={styles.td}>{formatAZ(b.latest_graded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Recent Submissions</h2>
        {recent_submissions.length === 0 ? (
          <div style={styles.placeholder}>
            No submissions yet. Graded results will appear here once processing begins.
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>VA Name</th>
                <th style={styles.th}>Batch</th>
                <th style={styles.th}>Submissions</th>
                <th style={styles.th}>Average Score</th>
                <th style={styles.th}>Rating</th>
                <th style={styles.th}>Graded At</th>
              </tr>
            </thead>
            <tbody>
              {recent_submissions.map((sub, idx) => (
                <tr key={idx}>
                  <td style={styles.td}>{sub.va_name}</td>
                  <td style={styles.td}>{sub.batch}</td>
                  <td style={styles.td}>{sub.submissions_count} / 75</td>
                  <td style={styles.td}>
                    <strong>{sub.average_score > 0 ? sub.average_score + '%' : '--'}</strong>
                  </td>
                  <td style={styles.td}>
                    {sub.rating ? (
                      <span style={styles.ratingBadge(sub.rating)}>{sub.rating}</span>
                    ) : '--'}
                  </td>
                  <td style={styles.td}>
                    {formatAZ(sub.graded_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
